import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from 'react/components/common/Tooltip/Tooltip';
import { columnClass } from '../utils';
import ColumnSelector from './ColumnSelector';

const TableHeader = ({
  visibleColumns,
  sortConfig,
  handleClickColumn,
  searchPosition,
  search,
  searchOpened,
  searchInputRef,
  handleToggleSearch,
  handleSearchChange,
  handleClearSearch,
  loading,
  viewArrayLength,
  // Column selector props
  columnsState,
  allColumns,
  columnSelectionOpened,
  setColumnSelectionOpened,
  setColumnSelectionHovered,
  handleSelectColumn,
  handleSelectAllColumns,
  handleLeavePencil,
  fixedSize,
  // Export props
  excel,
  exportFn,
  handleExport,
  id,
  tableTitle,
  // Max/Min props
  maxMin,
  isMaxed,
  handleResize,
  // CRUD props
  crud,
  crudTemplateHead,
}) => (
  <div className="row thead">
    {visibleColumns.map((column, index) => (
      <Tooltip
        key={column.id}
        content={column.tooltip}
        options={{ popperOptions: { modifiers: { flip: { enabled: false } } } }}
      >
        <div
          className={classNames('column', columnClass(column), `flex${column.flex || '1'}`)}
          style={{ gap: '4px', display: 'flex' }}
          onClick={() => column.sort !== false && handleClickColumn(column)}
        >
          {column.templateHead && <span dangerouslySetInnerHTML={{ __html: column.templateHead }} />}

          {/* Search */}
          {index === searchPosition && (
            <span className={classNames('search', { selected: search })} onClick={(e) => e.stopPropagation()}>
              <i className="icon-Search pointer" onClick={handleToggleSearch}></i>
              {!loading && searchOpened && (
                <span>
                  <input
                    ref={searchInputRef}
                    value={search}
                    onChange={handleSearchChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <i className="icon-X_CLEAR pointer" onClick={handleClearSearch}></i>
                </span>
              )}
            </span>
          )}

          {(index !== searchPosition || !searchOpened) && <span className="column-title">{column.title}</span>}

          {/* (number) */}
          {index === 0 && <span>({viewArrayLength})</span>}

          {/* Sort */}
          {column.sort !== false && (
            <span>
              {((sortConfig.order === column.id && sortConfig.reverse) ||
                (column.lockable && sortConfig.order === 'order')) && (
                <i title="sort down" className="icon sort icon-Triangle_Down"></i>
              )}
              {sortConfig.order === column.id && !sortConfig.reverse && (
                <i title="sort up" className="icon sort icon-Triangle_Up"></i>
              )}
            </span>
          )}
        </div>
      </Tooltip>
    ))}

    {/* CRUD */}
    {crud && (
      <div className="column crud">
        {crudTemplateHead && <span dangerouslySetInnerHTML={{ __html: crudTemplateHead }} />}
      </div>
    )}

    <div className="column scroll-td">
      {/* Export Excel */}
      {excel && (
        <a className="inline-block export" onClick={handleExport} title={`export ${id}`}>
          <i className="icon-download" style={{ width: '16px', height: '16px' }}></i>
        </a>
      )}

      {/* Export CSV */}
      {exportFn && !excel && (
        <a className="pointer table-options-link" onClick={handleExport} title={`export ${tableTitle}`}>
          <i className="icon-Export"></i>
        </a>
      )}

      {/* Max/Min */}
      {maxMin && (
        <a
          className="pointer table-options-link icon-button-v2-small"
          title={isMaxed ? 'Minimize' : 'Maximize'}
          onClick={handleResize}
        >
          <i className={classNames('max_min', isMaxed ? 'icon-minimize' : 'icon-maximize')}></i>
        </a>
      )}

      {/* Column Selection */}
      {!loading && !fixedSize && (
        <ColumnSelector
          columnsState={columnsState}
          allColumns={allColumns}
          columnSelectionOpened={columnSelectionOpened}
          setColumnSelectionOpened={setColumnSelectionOpened}
          setColumnSelectionHovered={setColumnSelectionHovered}
          handleSelectColumn={handleSelectColumn}
          handleSelectAllColumns={handleSelectAllColumns}
          handleLeavePencil={handleLeavePencil}
        />
      )}
    </div>
  </div>
);

TableHeader.propTypes = {
  visibleColumns: PropTypes.array.isRequired,
  sortConfig: PropTypes.object.isRequired,
  handleClickColumn: PropTypes.func.isRequired,
  searchPosition: PropTypes.number,
  search: PropTypes.string,
  searchOpened: PropTypes.bool,
  searchInputRef: PropTypes.object,
  handleToggleSearch: PropTypes.func,
  handleSearchChange: PropTypes.func,
  handleClearSearch: PropTypes.func,
  loading: PropTypes.bool,
  viewArrayLength: PropTypes.number,
  columnsState: PropTypes.array,
  allColumns: PropTypes.bool,
  columnSelectionOpened: PropTypes.bool,
  setColumnSelectionOpened: PropTypes.func,
  setColumnSelectionHovered: PropTypes.func,
  handleSelectColumn: PropTypes.func,
  handleSelectAllColumns: PropTypes.func,
  handleLeavePencil: PropTypes.func,
  fixedSize: PropTypes.bool,
  excel: PropTypes.bool,
  exportFn: PropTypes.func,
  handleExport: PropTypes.func,
  id: PropTypes.string,
  tableTitle: PropTypes.string,
  maxMin: PropTypes.bool,
  isMaxed: PropTypes.bool,
  handleResize: PropTypes.func,
  crud: PropTypes.bool,
  crudTemplateHead: PropTypes.string,
};

export default TableHeader;
